import { GroupInterface, MemberInterface } from "@swivl-mono/types";
import { SelectItem, SelectOption } from "@swivl-mono/ui";

/**
 * Info-icon copy for the coupled Location and Assignee fields (ENG-2961).
 * Shared by the Create Ticket surfaces and the detail panel so all three
 * explain the dependency the same way.
 */
export const LOCATION_SCOPE_TOOLTIP = "Sets which members can be assigned to this ticket.";

export const ASSIGNEE_SCOPE_TOOLTIP = "Members are shown based on the location set above. Members scoped to other locations appear greyed out.";

/**
 * Client-side mirror of the server's assignee rule (ENG-2882): a member can
 * be assigned to a ticket at `locationId` when one of their role
 * assignments' LocationsMatrix covers it — org-wide, the location directly,
 * or via the location's group. A ticket with no location is only assignable
 * to org-wide members.
 *
 * UX filter only — the server independently validates assignees on write.
 * (Approximation: role actions aren't loaded client-side, and `groups` is
 * the caller's own filtered list, so the group-scope match can miss for
 * locations outside the caller's scope; the server check is exact.)
 */
export function memberCanAccessTicketLocation(member: MemberInterface, orgId: number, locationId: number | null, groups: GroupInterface[]): boolean {
  const permissions = member.permissions ?? [];
  if (permissions.some((p) => p.locations?.orgId === orgId)) return true;
  if (locationId == null) return false;

  const groupId = groups.find((g) => (g.locations ?? []).some((l) => l.id === locationId))?.id;
  return permissions.some(
    (p) => (p.locations?.locationIds?.includes(locationId) ?? false) || (groupId != null && (p.locations?.groupIds?.includes(groupId) ?? false))
  );
}

/**
 * True when a member is not org-wide but is scoped to at least one location or
 * group — i.e. a member `memberCanAccessTicketLocation` hides while the draft
 * has no location, but that choosing a location could surface (ENG-2961).
 * Used to decide whether the Assignee field's "pick a Location first" hint is
 * worth showing; a member with no location scope at all is never surfaced by
 * any location, so it doesn't count.
 */
export function memberHasLocationScopedAccess(member: MemberInterface, orgId: number): boolean {
  const permissions = member.permissions ?? [];
  if (permissions.some((p) => p.locations?.orgId === orgId)) return false;
  return permissions.some((p) => (p.locations?.locationIds?.length ?? 0) > 0 || (p.locations?.groupIds?.length ?? 0) > 0);
}

/**
 * Display name for a member, falling back to their email and then their id.
 * Lives here rather than in a component so the option builder below and the
 * ticketing views can share one definition.
 */
export function getMemberDisplayName(member: MemberInterface): string {
  if (member.account?.firstName || member.account?.lastName) {
    return `${member.account?.firstName ?? ""} ${member.account?.lastName ?? ""}`.trim();
  }
  return member.account?.email ?? `Member #${member.id}`;
}

/** Flattens the caller's groups into one id -> location-name lookup. */
function locationNamesById(groups: GroupInterface[]): Map<number, string> {
  const names = new Map<number, string>();
  groups.forEach((group) => (group.locations ?? []).forEach((location) => names.set(location.id, location.name)));
  return names;
}

/**
 * Human-readable summary of where a member is scoped, e.g. "Building A" or
 * "Building A +2". Shown beneath an unavailable member's name so the row says
 * why they can't be assigned (ENG-2961).
 */
function describeMemberScope(member: MemberInterface, groups: GroupInterface[], locationNames: Map<number, string>): string {
  const names: string[] = [];
  const seen = new Set<string>();
  const add = (name?: string): void => {
    if (name && !seen.has(name)) {
      seen.add(name);
      names.push(name);
    }
  };

  (member.permissions ?? []).forEach((permission) => {
    (permission.locations?.locationIds ?? []).forEach((id) => add(locationNames.get(id)));
    (permission.locations?.groupIds ?? []).forEach((id) => add(groups.find((group) => group.id === id)?.name));
  });

  if (names.length === 0) return "";
  return names.length === 1 ? names[0] : `${names[0]} +${names.length - 1}`;
}

/**
 * Header for the unavailable section. Names the offending location when one
 * is set, so someone hunting for a member sees which location is excluding
 * them rather than only learning that a location matters at all.
 */
function unavailableSectionLabel(locationId: number | null, locationNames: Map<number, string>): string {
  if (locationId == null) return "Available after you select a location";
  const name = locationNames.get(locationId);
  return name ? `Not available for ${name}` : "Not available for this location";
}

export interface AssigneeOptionsParams {
  members: MemberInterface[];
  orgId: number;
  locationId: number | null;
  groups: GroupInterface[];
  /** Members that stay selectable regardless of scope, e.g. the current assignee. */
  alwaysEligibleMemberIds?: number[];
}

/**
 * Builds the Assignee dropdown's options for every ticketing surface.
 *
 * Members the current location rules out are listed rather than dropped
 * (ENG-2961): they render disabled, under a divider that names the reason,
 * with their own scope on a second line. Keeping them visible is what removes
 * the need for a separate "pick a Location first" hint — the answer sits
 * where the user is already looking, and dropdown search still finds them
 * because it matches the scope line as well as the name.
 *
 * Members with no location scope at all stay hidden: no location choice
 * would ever surface them, so listing them would only add noise.
 */
export function buildAssigneeOptions({ members, orgId, locationId, groups, alwaysEligibleMemberIds = [] }: AssigneeOptionsParams): SelectItem[] {
  const locationNames = locationNamesById(groups);
  const alwaysEligible = new Set(alwaysEligibleMemberIds);
  const eligible: SelectOption[] = [];
  const unavailable: SelectOption[] = [];

  members.forEach((member) => {
    const name = getMemberDisplayName(member);

    if (alwaysEligible.has(member.id) || memberCanAccessTicketLocation(member, orgId, locationId, groups)) {
      eligible.push({ label: name, value: String(member.id) });
      return;
    }

    if (!memberHasLocationScopedAccess(member, orgId)) return;

    const scope = describeMemberScope(member, groups, locationNames);
    unavailable.push({ disabled: true, label: name, sublabel: scope || undefined, value: String(member.id) });
  });

  if (unavailable.length === 0) return eligible;
  // Rule then group title: the divider separates the sections, the group's own
  // label styles the title. Search drops the divider on its own if the group
  // filters out entirely.
  return [...eligible, { type: "divider" }, { type: "group", label: unavailableSectionLabel(locationId, locationNames), options: unavailable }];
}
